import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingCart, ChevronRight, Lock, Loader2, Tag, X, Check, AlertCircle } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api, { apiErrorMessage } from "../services/api";
import CardPayButton from "../components/CardPayButton";
import { AlsoBought } from "../components/Recommendations";
import { productPath } from "../utils/links";
import DeliveryAddressBox from "../components/DeliveryAddressBox";
import { useShippingQuote, ShippingQuoteNote, shippingLabel } from "../hooks/useShippingQuote";
import { ShippingChoice } from "../components/ShippingChoice";

const fmt = (n) => (n ?? 0).toFixed(2).replace(".", ",");

const NewsletterOptIn = ({ user }) => {
  const [state, setState] = useState(null);
  useEffect(() => { api.get("/newsletter/status").then(({ data }) => setState(data.subscribed ? "already" : "idle")).catch(() => setState("idle")); }, [user?.id]);
  if (state === null || state === "already") return null;
  const subscribe = async () => { setState("busy"); try { await api.post("/newsletter", { email: user.email, source: "commande" }); setState("done"); } catch { setState("idle"); } };
  return (
    <label data-testid="cart-newsletter-label" className={`flex items-start gap-2.5 text-[13px] cursor-pointer mt-5 border rounded-lg px-3 py-2.5 transition-colors ${state === "done" ? "bg-emerald-950/30 border-emerald-900/50 text-emerald-300" : "bg-[#0D0F14] border-[#1E232D] text-slate-300"}`}>
      <input data-testid="cart-newsletter" type="checkbox" disabled={state !== "idle"} checked={state === "done" || state === "busy"} onChange={subscribe} className="w-4 h-4 mt-0.5 accent-[#E01F1F]" />
      <span>{state === "done" ? "C'est noté ! Vous recevrez nos promos et conseils d'entretien." : "Recevoir les promos et conseils d'entretien KTSauto par email (1 par mois max)."}</span>
    </label>
  );
};

const CartPage = () => {
  const { items, removeItem, updateQty, total, count, clearCart, createOrder, refresh } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);

  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [settings, setSettings] = useState({ free_shipping_threshold: 79, shipping_cost: 5.9, free_shipping_enabled: true });
  const [paypalCfg, setPaypalCfg] = useState({ configured: false, client_id: "", currency: "EUR" });
  const [stripeCfg, setStripeCfg] = useState({ configured: null });
  const [payError, setPayError] = useState(() => new URLSearchParams(window.location.search).get("paiement") === "annule" ? "Paiement par carte annulé. Votre panier est conservé." : "");

  React.useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings({
      free_shipping_threshold: data.free_shipping_threshold ?? 79,
      free_shipping_enabled: data.free_shipping_enabled !== false,
      shipping_cost: data.shipping_cost ?? 5.9,
    })).catch(() => {});
    api.get("/paypal/config").then(({ data }) => setPaypalCfg(data)).catch(() => {});
    api.get("/payments/stripe/config").then(({ data }) => setStripeCfg(data)).catch(() => setStripeCfg({ configured: false }));
  }, []);

  const extraShipping = items.reduce((s, i) => s + (i.availability?.extra_shipping_ttc || 0), 0);
  const unavailable = items.filter((i) => i.availability && !i.availability.available);
  const [shipKey, setShipKey] = useState(0);
  const quote = useShippingQuote(user, items, "perso", shipKey);
  const shippingPending = !!(quote && quote.method === "boxtal" && quote.price == null);
  const baseShipping = quote ? (quote.price ?? 0) : ((settings.free_shipping_enabled && total > settings.free_shipping_threshold) || total === 0 ? 0 : settings.shipping_cost);
  const shipping = baseShipping + extraShipping;
  const discount = promo ? Math.min(promo.discount, total) : 0;
  const grandTotal = Math.max(0, total - discount) + shipping;

  const applyPromo = async (e) => {
    e.preventDefault();
    if (!promoInput.trim()) return;
    setPromoLoading(true); setPromoError("");
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput.trim(), subtotal: total });
      setPromo(data);
    } catch (err) {
      setPromo(null);
      setPromoError(apiErrorMessage(err, "Code promo invalide."));
    } finally { setPromoLoading(false); }
  };

  const removePromo = () => { setPromo(null); setPromoInput(""); setPromoError(""); };

  const checkout = async () => {
    if (!user) { navigate("/connexion?redirect=panier"); return; }
    setPlacing(true);
    try {
      await createOrder(promo ? promo.code : null);
      navigate("/compte?commande=ok");
    } catch (e) {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1440px] mx-auto px-5 py-20 text-center">
        <div className="w-20 h-20 rounded-full bg-[#181C26] flex items-center justify-center mx-auto mb-5">
          <ShoppingCart size={34} className="text-slate-500" />
        </div>
        <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl mb-2">Votre panier est vide</h1>
        <p className="text-slate-400 mb-6">Découvrez nos milliers de pièces auto.</p>
        <Link to="/" className="btn-orange inline-block rounded-lg px-8 py-3.5 font-bold">Continuer mes achats</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-5 py-8">
      <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-5">
        <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
        <ChevronRight size={15} />
        <span className="text-slate-50 font-semibold">Mon panier</span>
      </div>
      <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-4xl mb-6">Mon panier ({count})</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.id} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-4 flex gap-4 items-center">
              <Link to={productPath(item)} className="w-24 h-24 rounded-lg bg-[#08090C] overflow-hidden shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <span className="text-[12px] font-bold text-[#FF3B3B] uppercase">{item.brand}</span>
                <Link to={productPath(item)} className="block font-semibold text-slate-50 hover:text-[#FF3B3B] transition-colors line-clamp-2">{item.name}</Link>
                <span className="text-[13px] text-slate-400">Réf. {item.ref}</span>
                {item.availability && <div className={`text-[12px] font-semibold ${item.availability.available ? (item.availability.status === "in_stock" ? "text-emerald-400" : "text-amber-300") : "text-red-400"}`} data-testid={`cart-avail-${item.id}`}>{item.availability.label}{item.availability.delivery_date && item.availability.available ? ` · livraison estimée le ${new Date(item.availability.delivery_date).toLocaleDateString("fr-FR")}` : ""}</div>}
              </div>
              <div className="flex items-center border-2 border-[#222735] rounded-lg">
                <button onClick={() => updateQty(item.id, item.qty - 1)} className="px-2.5 py-2 text-slate-50 hover:text-[#FF3B3B]"><Minus size={16} /></button>
                <span className="w-8 text-center font-bold text-slate-50">{item.qty}</span>
                <button onClick={() => updateQty(item.id, item.qty + 1)} className="px-2.5 py-2 text-slate-50 hover:text-[#FF3B3B]"><Plus size={16} /></button>
              </div>
              <div className="text-right w-24">
                <div className="font-black text-slate-50">{(item.price * item.qty).toFixed(2).replace(".", ",")}&nbsp;€</div>
                <button onClick={() => removeItem(item.id)} className="text-slate-500 hover:text-red-500 mt-2 inline-flex items-center gap-1 text-[13px]"><Trash2 size={15} /> Retirer</button>
              </div>
            </div>
          ))}
          <button onClick={clearCart} className="text-[14px] text-slate-400 hover:text-red-500">Vider le panier</button>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-6 sticky top-6">
            <h2 className="font-bold text-slate-50 text-xl mb-4">Récapitulatif</h2>
            {promo ? (
              <div data-testid="promo-applied" className="flex items-center justify-between bg-emerald-950/40 border border-emerald-800/50 rounded-lg px-3 py-2.5 mb-4">
                <span className="flex items-center gap-2 text-[14px] font-semibold text-emerald-400"><Check size={16} /> {promo.code} appliqué</span>
                <button data-testid="promo-remove" onClick={removePromo} className="text-emerald-400 hover:text-red-500"><X size={16} /></button>
              </div>
            ) : (
              <form onSubmit={applyPromo} className="mb-4">
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F]">
                    <Tag size={16} className="text-slate-500" />
                    <input data-testid="promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Code promo" className="w-full py-2.5 px-2 outline-none text-slate-50 uppercase" />
                  </div>
                  <button data-testid="promo-apply" type="submit" disabled={promoLoading} className="bg-[#181C26] text-white font-bold px-4 rounded-lg hover:bg-[#E01F1F] transition-colors disabled:opacity-60">
                    {promoLoading ? <Loader2 size={16} className="animate-spin" /> : "OK"}
                  </button>
                </div>
                {promoError && <p data-testid="promo-error" className="text-red-400 text-[13px] mt-2">{promoError}</p>}
              </form>
            )}

            <div className="space-y-3 text-[15px]">
              <div className="flex justify-between"><span className="text-slate-400">Sous-total</span><span className="font-semibold text-slate-50">{fmt(total)}&nbsp;€</span></div>
              {discount > 0 && (
                <div className="flex justify-between" data-testid="promo-discount-line"><span className="text-emerald-400">Remise ({promo.code})</span><span className="font-semibold text-emerald-400">−{fmt(discount)}&nbsp;€</span></div>
              )}
              <div className="flex justify-between"><span className="text-slate-400">Livraison</span><span className={`font-semibold ${shippingPending ? "text-amber-300" : "text-slate-50"}`} data-testid="cart-shipping-value">{shippingLabel(shipping, quote)}</span></div>
              <ShippingQuoteNote quote={quote} />
              {extraShipping > 0 && (<div className="text-[12px] text-amber-300" data-testid="cart-extra-shipping">dont {extraShipping.toFixed(2).replace(".", ",")}&nbsp;€ de supplément transport (pièce importée, expédiée séparément)</div>)}
              {unavailable.length > 0 && (<div className="text-[13px] text-red-400 font-semibold" data-testid="cart-unavailable">Pièce arrêtée par le fabricant, retirez-la pour commander : {unavailable.map((i) => i.name).join(", ")}</div>)}
              {settings.free_shipping_enabled && !shippingPending && shipping - extraShipping > 0 && (<div className="text-[13px] text-[#FF3B3B]">Plus que {(settings.free_shipping_threshold - total).toFixed(2).replace(".", ",")}&nbsp;€ pour la livraison offerte&nbsp;!</div>)}
              <div className="border-t border-[#1E232D] pt-3 flex justify-between items-end">
                <span className="font-bold text-slate-50">Total TTC</span>
                <span data-testid="cart-total" className="font-black text-slate-50 text-2xl">{fmt(grandTotal)}&nbsp;€</span>
              </div>
            </div>

            {payError && (
              <div data-testid="pay-error" className="flex items-start gap-2 text-red-400 text-[13px] mt-4 bg-red-950/40 border border-red-900/50 rounded-lg p-3">
                <AlertCircle size={16} className="shrink-0 mt-0.5" /> {payError}
              </div>
            )}

            {user && <NewsletterOptIn user={user} />}
            {!user ? (
              <button onClick={checkout} data-testid="checkout-login-btn" className="btn-orange w-full rounded-lg py-3.5 font-bold text-lg mt-5 flex items-center justify-center gap-2">
                <Lock size={18} /> Se connecter pour commander
              </button>
            ) : (
              <div data-testid="payment-methods" className="mt-5 space-y-3">
                <DeliveryAddressBox onReady={setHasAddress} />
                {hasAddress && <ShippingChoice items={items} channel="perso" onChange={() => setShipKey((k) => k + 1)} />}
                {stripeCfg.configured && <CardPayButton promoCode={promo ? promo.code : null} channel="perso" disabled={placing || !hasAddress || unavailable.length > 0 || shippingPending} onError={setPayError} />}
                {paypalCfg.configured && paypalCfg.client_id && (
                  <div data-testid="paypal-buttons" className={stripeCfg.configured ? "mt-3" : ""}>
                    <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: paypalCfg.currency || "EUR", intent: "capture" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                        disabled={placing}
                        createOrder={async () => {
                          setPayError("");
                          const { data } = await api.post("/paypal/create-order", { promo_code: promo ? promo.code : null });
                          return data.id;
                        }}
                        onApprove={async (data) => {
                          setPlacing(true);
                          try {
                            await api.post(`/paypal/capture-order/${data.orderID}`, { promo_code: promo ? promo.code : null });
                            await refresh();
                            navigate("/compte?commande=ok");
                          } catch (err) {
                            setPayError(apiErrorMessage(err, "Le paiement n'a pas pu être finalisé."));
                            setPlacing(false);
                          }
                        }}
                        onError={() => setPayError("Une erreur est survenue avec PayPal. Veuillez réessayer.")}
                        onCancel={() => setPayError("Paiement annulé.")}
                      />
                    </PayPalScriptProvider>
                  </div>
                )}
                {stripeCfg.configured === false && !paypalCfg.configured && (
                  <button onClick={checkout} disabled={placing || unavailable.length > 0 || shippingPending} data-testid="checkout-btn" className="btn-orange w-full rounded-lg py-3.5 font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-70">
                    {placing ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />} Passer commande
                  </button>
                )}
              </div>
            )}
            <Link to="/" className="block text-center text-[14px] text-[#FF3B3B] font-semibold hover:underline mt-4">Continuer mes achats</Link>
          </div>
        </div>
      </div>
      <AlsoBought productId={items[0]?.id} compact />
    </div>
  );
};

export default CartPage;
